import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { professionalCost, professionalDuration, useProfessionalAvailability, type ProfessionalSummary } from '../lib/professional.js';

const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/** Keep the server's receipt visible after the room and its live controls close: every Professional session of the call, not just the last. */
export function ProfessionalReceipt({ meetingId, userId }: { meetingId: string; userId: string }) {
  const availability = useProfessionalAvailability(userId);
  const [receipt, setReceipt] = useState<{ meetingId: string; userId: string; summary: ProfessionalSummary } | null>(null);
  useEffect(() => {
    if (!availability?.eligible) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const refresh = async () => {
      attempts++;
      try {
        const summary = await api.professional.summary(meetingId);
        if (!alive) return;
        setReceipt({ meetingId, userId, summary });
        if (summary.eligible && summary.pending && attempts < 15) timer = setTimeout(() => void refresh(), 2000);
      } catch { if (alive && attempts < 15) timer = setTimeout(() => void refresh(), 2000); }
    };
    void refresh();
    return () => { alive = false; clearTimeout(timer); };
  }, [meetingId, userId, availability?.eligible]);
  const summary = receipt?.meetingId === meetingId && receipt.userId === userId ? receipt.summary : null;
  if (!availability?.eligible || !summary?.eligible || summary.sessions.length === 0) return null;
  return (
    <div className="room-professional-receipt" role="status">
      <p>{summary.pending ? 'Professional final usage pending.' : 'Professional stopped.'} {summary.sessions.length === 1 ? '1 session' : `${summary.sessions.length} sessions`} · {summary.totalMinutes} min
        {summary.totalMicros !== null && <> · {summary.pending ? 'about ' : ''}{professionalCost(summary.totalMicros)}</>}</p>
      <ol>
        {summary.sessions.map((s, i) => (
          <li key={s.id}>Session {i + 1}: {time(s.startedAt)}{s.endedAt ? ` to ${time(s.endedAt)}` : ' (running)'} · {professionalDuration(s.durationSeconds)}
            {s.finalMicros !== null && <> · {professionalCost(s.finalMicros)}</>}</li>
        ))}
      </ol>
    </div>
  );
}
